/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type {
  MyHubRecordingHighlightState,
  TMyHubRecordingHighlightItem
} from "./recordingHighlight.types";
import type { TCarouselItem, TMyHubActionType } from "../../../model/types";
import { UnityRootState } from "../../UnityAppStore";
import type { UnityAppDispatch } from "../../UnityAppStore";
import {
  buildRecordingHighlightScheduleLabel,
  toRecordingHighlightItem
} from "./recordingHighlightMappers";
import { resolveChannelName } from "../myHubMappers";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "recordingHighlightSlice";

/** Focus always opens on the item's first button. */
const INITIAL_ACTION_INDEX: number = 0;

const INITIAL_STATE: MyHubRecordingHighlightState = {
  isVisible: false,
  isClosing: false,
  item: null,
  focusedActionIndex: INITIAL_ACTION_INDEX,
  listName: "",
  listPosition: 0,
  tilePosition: 0
};

/** {@link setRecordingHighlightScheduleLabel}'s payload. */
interface TSetRecordingHighlightScheduleLabelPayload {
  /** The item the line was composed for — ignored if another is now open. */
  id: string;
  scheduleLabel: string;
}

/** {@link showRecordingHighlight}'s payload — the item plus where it opened from. */
interface TShowRecordingHighlightPayload {
  item: TMyHubRecordingHighlightItem;
  listName: string;
  listPosition: number;
  tilePosition: number;
}

/**
 * Clamps a requested button index into the open item's rendered range.
 *
 * Clamping rather than wrapping: with one row of buttons, wrapping from the
 * last back to the first makes a single Right press cross the whole row, which
 * reads as a mis-press on a remote. The ends are dead stops instead.
 *
 * The range comes from the item rather than a fixed table because the buttons
 * are whatever the service said the recording affords.
 *
 * @param state - Current popup state, for the open item's action count.
 * @param index - Requested index, possibly out of range.
 * @returns An index that exists in the open item's `availableActions`, or the
 *   initial index when nothing is open.
 */
const clampActionIndex = (
  state: MyHubRecordingHighlightState,
  index: number
): number => {
  const actionCount: number =
    state.item === null ? 0 : state.item.availableActions.length;
  if (actionCount === 0) {
    return INITIAL_ACTION_INDEX;
  }
  return Math.min(actionCount - 1, Math.max(0, index));
};

const recordingHighlightSlice = createSlice({
  name: "recordingHighlight",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Opens the popup for one recording. Callable from any screen — the
     * container is mounted once in the app shell, so no screen needs to host it.
     */
    showRecordingHighlight: (
      state: MyHubRecordingHighlightState,
      action: PayloadAction<TShowRecordingHighlightPayload>
    ): void => {
      state.isVisible = true;
      state.isClosing = false;
      state.item = action.payload.item;
      state.focusedActionIndex = INITIAL_ACTION_INDEX;
      state.listName = action.payload.listName;
      state.listPosition = action.payload.listPosition;
      state.tilePosition = action.payload.tilePosition;
    },

    /**
     * Starts closing the popup — the one action every dismiss path dispatches.
     *
     * Input leaves immediately (`isVisible` false pops the modal controller),
     * but the item is held so the sheet has something to paint while it sinks.
     * The teardown is a second step, {@link finishRecordingHighlightDismiss},
     * which the container schedules for the end of the transition. Focus is held
     * too, so the button the user pressed keeps its fill on the way out instead
     * of snapping back to Play.
     */
    dismissRecordingHighlight: (state: MyHubRecordingHighlightState): void => {
      // Guarded so a second BACK — or a dimmer click on top of one — cannot
      // restart the sink and stretch the exit.
      if (!state.isVisible) {
        return;
      }
      state.isVisible = false;
      state.isClosing = true;
    },

    /**
     * Completes the dismiss once the sheet has sunk: unmounts the popup and
     * clears the item, so a stale poster can never flash behind the next open,
     * and focus resets for the same reason.
     *
     * Ignored unless a dismiss is actually in flight. A timer fired for a popup
     * that has since been reopened would otherwise blank the new one.
     */
    finishRecordingHighlightDismiss: (
      state: MyHubRecordingHighlightState
    ): void => {
      if (!state.isClosing) {
        return;
      }
      state.isClosing = false;
      state.item = null;
      state.focusedActionIndex = INITIAL_ACTION_INDEX;
      state.listName = "";
      state.listPosition = 0;
      state.tilePosition = 0;
    },

    /**
     * Completes the movie card's airing line once the station's name has
     * resolved. Dropped when the popup has since closed or moved on to another
     * item, so a slow lookup can never write one recording's channel onto the
     * next.
     */
    setRecordingHighlightScheduleLabel: (
      state: MyHubRecordingHighlightState,
      action: PayloadAction<TSetRecordingHighlightScheduleLabelPayload>
    ): void => {
      if (
        !state.isVisible ||
        state.item === null ||
        state.item.id !== action.payload.id
      ) {
        return;
      }
      state.item.scheduleLabel = action.payload.scheduleLabel;
    },

    /** Points focus at a specific button — the pointer-hover path. */
    focusRecordingHighlightAction: (
      state: MyHubRecordingHighlightState,
      action: PayloadAction<number>
    ): void => {
      state.focusedActionIndex = clampActionIndex(state, action.payload);
    },

    /** NAV_RIGHT — moves one button towards Details. */
    focusNextRecordingHighlightAction: (
      state: MyHubRecordingHighlightState
    ): void => {
      state.focusedActionIndex = clampActionIndex(
        state,
        state.focusedActionIndex + 1
      );
    },

    /** NAV_LEFT — moves one button towards Play. */
    focusPreviousRecordingHighlightAction: (
      state: MyHubRecordingHighlightState
    ): void => {
      state.focusedActionIndex = clampActionIndex(
        state,
        state.focusedActionIndex - 1
      );
    }
  }
});

export const {
  showRecordingHighlight,
  dismissRecordingHighlight,
  finishRecordingHighlightDismiss,
  setRecordingHighlightScheduleLabel,
  focusRecordingHighlightAction,
  focusNextRecordingHighlightAction,
  focusPreviousRecordingHighlightAction
} = recordingHighlightSlice.actions;

export const selectIsRecordingHighlightVisible = (
  state: UnityRootState
): boolean => state.recordingHighlight.isVisible;

/**
 * Whether the popup is sinking back off screen. Kept mounted while true, but it
 * no longer owns input — see {@link MyHubRecordingHighlightState.isClosing}.
 */
export const selectIsRecordingHighlightClosing = (
  state: UnityRootState
): boolean => state.recordingHighlight.isClosing;

export const selectRecordingHighlightItem = (
  state: UnityRootState
): TMyHubRecordingHighlightItem | null => state.recordingHighlight.item;

export const selectRecordingHighlightFocusedActionIndex = (
  state: UnityRootState
): number => state.recordingHighlight.focusedActionIndex;

/**
 * `list_name` of the carousel row the open popup was raised from — see
 * {@link MyHubRecordingHighlightState.listName}.
 */
export const selectRecordingHighlightListName = (
  state: UnityRootState
): string => state.recordingHighlight.listName;

/**
 * `list_position` of the carousel row the open popup was raised from — see
 * {@link MyHubRecordingHighlightState.listPosition}.
 */
export const selectRecordingHighlightListPosition = (
  state: UnityRootState
): number => state.recordingHighlight.listPosition;

/**
 * `tile_position` of the tile the open popup was raised from — see
 * {@link MyHubRecordingHighlightState.tilePosition}.
 */
export const selectRecordingHighlightTilePosition = (
  state: UnityRootState
): number => state.recordingHighlight.tilePosition;

/**
 * The action the focused button would fire, or `undefined` when nothing is open
 * or focus is out of range. Lets the controller act on the focused button
 * without duplicating the item's button order.
 */
export const selectFocusedRecordingHighlightAction = (
  state: UnityRootState
): TMyHubActionType | undefined => {
  const item: TMyHubRecordingHighlightItem | null =
    state.recordingHighlight.item;
  return item === null
    ? undefined
    : item.availableActions[state.recordingHighlight.focusedActionIndex];
};

// ── Thunks ───────────────────────────────────────────────────────────────────

/**
 * Opens the recording highlight for one My Hub tile.
 *
 * A thin thunk rather than a bare action creator only so callers keep
 * dispatching it the same way regardless of what building the item takes —
 * `toRecordingHighlightItem` is synchronous today, but was not always (it
 * used to await the tile's station call sign for an airing line the current
 * design no longer shows).
 *
 * @param tile - The mapped My Hub tile the press came from.
 * @param listName - The carousel row's title the tile was pressed from.
 * @param listPosition - The row's index within the visible grid.
 * @param tilePosition - The tile's index within that row.
 */
export const openRecordingHighlight =
  (
    tile: TCarouselItem,
    listName: string,
    listPosition: number,
    tilePosition: number
  ) =>
  (dispatch: UnityAppDispatch): void => {
    const item: TMyHubRecordingHighlightItem = toRecordingHighlightItem(tile);
    dispatch(
      showRecordingHighlight({
        item,
        listName,
        listPosition,
        tilePosition
      })
    );

    // The movie card's airing line names the channel, which lives in the
    // channel list rather than on the tile. The popup opens straight away
    // without it, and the clause is added when the lookup lands; a miss simply
    // leaves the line without one (resolveChannelName never rejects).
    if (item.cardType !== "movie" || item.scheduleLabel === "") {
      return;
    }
    resolveChannelName(tile.stationId).then((channelName: string): void => {
      if (channelName === "") {
        return;
      }
      dispatch(
        setRecordingHighlightScheduleLabel({
          id: item.id,
          scheduleLabel: buildRecordingHighlightScheduleLabel(tile, channelName)
        })
      );
    });
  };

export default recordingHighlightSlice.reducer;
